import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getEvents, createEvent } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Calendar, Clock, MapPin, Loader2, Plus, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const EventsPage = () => {
  const { language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Submission form state
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title_en: '', title_pa: '', description_en: '', description_pa: '',
    location_en: '', location_pa: '', date: '', time: ''
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await getEvents();
        setEvents(data);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const getText = (item, field) => {
    return language === 'pa' ? item[`${field}_pa`] : item[`${field}_en`];
  };

  const isUpcoming = (date) => new Date(date) >= new Date();

  const sortedEvents = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEvent.title_en || !newEvent.date || !newEvent.time || !newEvent.location_en) {
      toast.error(language === 'pa' ? 'ਲੋੜੀਂਦੇ ਖੇਤਰ ਭਰੋ' : 'Please fill required fields');
      return;
    }
    setSubmitting(true);
    try {
      await createEvent({
        ...newEvent,
        title_pa: newEvent.title_pa || newEvent.title_en,
        description_pa: newEvent.description_pa || newEvent.description_en,
        location_pa: newEvent.location_pa || newEvent.location_en,
        date: new Date(newEvent.date).toISOString()
      }, false); // false = public submission (pending)
      setSubmitted(true);
      setTimeout(() => {
        setSubmitOpen(false);
        setSubmitted(false);
        setNewEvent({ title_en: '', title_pa: '', description_en: '', description_pa: '', location_en: '', location_pa: '', date: '', time: '' });
      }, 2000);
    } catch (error) {
      toast.error(language === 'pa' ? 'ਗਲਤੀ ਹੋਈ' : 'Error submitting');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {language === 'pa' ? 'ਸਮਾਗਮ ਕੈਲੰਡਰ' : 'Events Calendar'}
            </h1>
            <p className="text-amber-100">
              {language === 'pa' ? 'ਪਿੰਡ ਦੇ ਆਉਣ ਵਾਲੇ ਸਮਾਗਮ' : 'Upcoming events and festivals'}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          </div>
        ) : (
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-amber-200 -translate-x-1/2 hidden md:block"></div>
            
            <div className="space-y-8">
              {sortedEvents.map((event, index) => {
                const upcoming = isUpcoming(event.date);
                const eventDate = new Date(event.date);
                
                return (
                  <div key={event.id} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                    {/* Timeline Dot */}
                    <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-amber-500 border-4 border-white shadow-md z-10"></div>
                    
                    {/* Date Card */}
                    <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:text-left md:pr-12' : 'md:text-right md:pl-12'}`}>
                      <div className={`inline-flex flex-col items-center p-3 rounded-xl ${
                        upcoming ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <span className="text-xs font-medium uppercase">
                          {eventDate.toLocaleDateString('en', { month: 'short' })}
                        </span>
                        <span className="text-2xl font-bold">
                          {eventDate.getDate()}
                        </span>
                        <span className="text-xs">
                          {eventDate.getFullYear()}
                        </span>
                      </div>
                    </div>
                    
                    {/* Event Card */}
                    <div className="md:w-1/2">
                      <Card className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 ${
                        upcoming ? '' : 'opacity-60'
                      }`}>
                        <CardContent className="p-6">
                          {upcoming && (
                            <span className="inline-block bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded-full mb-3">
                              {language === 'pa' ? 'ਆਉਣ ਵਾਲਾ' : 'Upcoming'}
                            </span>
                          )}
                          <h3 className="text-xl font-bold text-gray-900 mb-3">
                            {getText(event, 'title')}
                          </h3>
                          <div className="space-y-2 text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-amber-500" />
                              {event.time}
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-amber-500" />
                              {getText(event, 'location')}
                            </div>
                          </div>
                          <p className="text-gray-600 mt-4">
                            {getText(event, 'description')}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!loading && events.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {language === 'pa' ? 'ਕੋਈ ਸਮਾਗਮ ਨਹੀਂ' : 'No events scheduled'}
            </h3>
          </div>
        )}
      </div>

      {/* Floating Add Button */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogTrigger asChild>
          <Button 
            className="fixed bottom-20 right-6 w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-600 shadow-lg hover:shadow-xl transition-all duration-300 z-50"
            size="icon"
          >
            <Plus className="h-6 w-6" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {language === 'pa' ? 'ਸਫਲਤਾਪੂਰਵਕ ਜਮ੍ਹਾਂ!' : 'Submitted Successfully!'}
              </h3>
              <p className="text-gray-500 text-sm">
                {language === 'pa' 
                  ? 'ਐਡਮਿਨ ਦੀ ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ ਦਿਖਾਈ ਦੇਵੇਗਾ'
                  : 'Will be visible after admin approval'}
              </p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  {language === 'pa' ? 'ਸਮਾਗਮ ਸ਼ਾਮਲ ਕਰੋ' : 'Submit Event'}
                </DialogTitle>
                <DialogDescription>
                  {language === 'pa' 
                    ? 'ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ ਹੀ ਦਿਖਾਈ ਦੇਵੇਗਾ'
                    : 'Event will appear after admin approval'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <Input 
                  placeholder={language === 'pa' ? 'ਸਿਰਲੇਖ (ਅੰਗਰੇਜ਼ੀ) *' : 'Title (English) *'}
                  value={newEvent.title_en}
                  onChange={(e) => setNewEvent({...newEvent, title_en: e.target.value})}
                />
                <Input 
                  placeholder={language === 'pa' ? 'ਸਿਰਲੇਖ (ਪੰਜਾਬੀ)' : 'Title (Punjabi)'}
                  value={newEvent.title_pa}
                  onChange={(e) => setNewEvent({...newEvent, title_pa: e.target.value})}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input 
                    type="date"
                    placeholder={language === 'pa' ? 'ਤਾਰੀਖ਼ *' : 'Date *'}
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                  />
                  <Input 
                    placeholder={language === 'pa' ? 'ਸਮਾਂ * (ਉਦਾ: 10:00 AM)' : 'Time * (e.g., 10:00 AM)'}
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                  />
                </div>
                <Input 
                  placeholder={language === 'pa' ? 'ਥਾਂ (ਅੰਗਰੇਜ਼ੀ) *' : 'Location (English) *'}
                  value={newEvent.location_en}
                  onChange={(e) => setNewEvent({...newEvent, location_en: e.target.value})}
                />
                <Input 
                  placeholder={language === 'pa' ? 'ਥਾਂ (ਪੰਜਾਬੀ)' : 'Location (Punjabi)'}
                  value={newEvent.location_pa}
                  onChange={(e) => setNewEvent({...newEvent, location_pa: e.target.value})}
                />
                <Textarea 
                  placeholder={language === 'pa' ? 'ਵੇਰਵਾ (ਅੰਗਰੇਜ਼ੀ)' : 'Description (English)'}
                  value={newEvent.description_en}
                  onChange={(e) => setNewEvent({...newEvent, description_en: e.target.value})}
                  rows={2}
                />
                <Textarea 
                  placeholder={language === 'pa' ? 'ਵੇਰਵਾ (ਪੰਜਾਬੀ)' : 'Description (Punjabi)'}
                  value={newEvent.description_pa}
                  onChange={(e) => setNewEvent({...newEvent, description_pa: e.target.value})}
                  rows={2}
                />
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setSubmitOpen(false)}
                  >
                    {language === 'pa' ? 'ਰੱਦ ਕਰੋ' : 'Cancel'}
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-amber-500 hover:bg-amber-600"
                    disabled={submitting}
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'pa' ? 'ਜਮ੍ਹਾਂ ਕਰੋ' : 'Submit')}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsPage;
